import { Component, computed, DestroyRef, effect, inject, input, output, Signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FieldTree, FormField } from '@angular/forms/signals';
import { AuthService } from '@app/shared/services/auth.service';
import { CompaniesDataService } from '@app/shared/services/companies-data.service';
import { LocationsDataService } from '@app/shared/services/locations-data.service';
import { ProjectsDataService } from '@app/shared/services/projects-data.service';
import { TasksDataService } from '@app/shared/services/tasks-data.service';
import { SortDirection } from '@app/shared/types/enums/sort-direction.enum';
import { TaskSortField } from '@app/shared/types/enums/task-sort-field';
import { Location } from '@app/shared/types/models/location/location.model';
import { TimeReport } from '@app/shared/types/models/reports/time-report';
import { WorkTimeReport } from '@app/shared/types/models/reports/work-time-report';
import { DropdownItem } from '@app/shared/types/models/shared/dropdown-item';
import { Task } from '@app/shared/types/models/task/task.model';
import { UiEmptyStateComponent } from '@app/shared/ui/empty-state/ui-empty-state.component';
import { UiSelectComponent } from '@app/shared/ui/select/ui-select.component';
import { UiTreeSelectComponent } from '@app/shared/ui/tree-select/ui-tree-select.component';
import { proffeoBuildTree } from '@app/shared/ui/types/tree.types';
import { TranslatePipe } from '@ngx-translate/core';
import { ProjectTaskModel } from '../../time-report-form.types';

/** Company / Project / Task (and labels) known up front from a Task or an edited report. */
export interface ProjectTaskPreselection {
  companyId?: string;
  companyName?: string;
  projectId?: string;
  projectName?: string;
  taskId?: string;
  taskName?: string;
  locationId?: string;
}

/** Preselection from the Task context (wins) or from an edited work-time report; empty for a new report. */
export function buildProjectTaskPreselection(
  task: Task | undefined,
  report: TimeReport | undefined,
  projectsService: Pick<ProjectsDataService, 'getById'>
): ProjectTaskPreselection {
  if (task) {
    const project = task.project?.();
    const company = project?.company();

    return {
      companyId: company?.id,
      companyName: company?.name,
      projectId: project?.id,
      projectName: project?.name,
      taskId: task.id,
      taskName: task.name
    };
  }

  if (!(report instanceof WorkTimeReport)) {
    return {};
  }

  // WorkTimeReport has no company - have to get it from the project
  const company = report.projectId ? projectsService.getById(report.projectId)()?.company() : undefined;

  return {
    companyId: company?.id,
    companyName: company?.name,
    projectId: report.projectId,
    projectName: report.project?.name,
    taskId: report.taskId,
    taskName: report.task?.name,
    locationId: report.locationId
  };
}

export function toProjectTaskModel(preselection: ProjectTaskPreselection): ProjectTaskModel {
  return {
    company: preselection.companyId ?? null,
    project: preselection.projectId ?? null,
    task: preselection.taskId ?? null,
    location: preselection.locationId ?? null
  };
}

function selectedId(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

@Component({
  selector: 'proffeo-project-task-selector',
  templateUrl: './project-task-selector.component.html',
  imports: [FormField, TranslatePipe, UiSelectComponent, UiTreeSelectComponent, UiEmptyStateComponent]
})
export class ProjectTaskSelectorComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly companiesService = inject(CompaniesDataService);
  private readonly projectsService = inject(ProjectsDataService);
  private readonly tasksService = inject(TasksDataService);
  private readonly locationsService = inject(LocationsDataService);

  protected readonly companyId: Signal<string | null> = computed(() => this.selection().company().value());
  protected readonly projectId: Signal<string | null> = computed(() => this.selection().project().value());
  protected readonly taskId: Signal<string | null> = computed(() => this.selection().task().value());
  protected readonly locationId: Signal<string | null> = computed(() => this.selection().location().value());

  protected readonly companyItems: Signal<DropdownItem<string>[]> = computed(() => {
    const userId = this.authService.currentUser()?.id;
    const companies = userId ? (this.companiesService.getUserCompanies(userId)() ?? []) : [];
    const items = companies.map(company => ({ value: company.id, label: company.name }));
    const preselection = this.preselection();

    return this.withPreselectedOption(items, preselection.companyId, preselection.companyName);
  });

  protected readonly projectItems: Signal<DropdownItem<string>[]> = computed(() => {
    const companyId = this.companyId();
    const projects = companyId ? (this.projectsService.getCompanyProjects(companyId)() ?? []) : [];
    const items = projects.map(project => ({ value: project.id, label: project.name }));
    const preselection = this.preselection();

    return this.withPreselectedOption(items, preselection.projectId, preselection.projectName);
  });

  protected readonly taskItems: Signal<DropdownItem<string>[]> = computed(() => {
    const projectId = this.projectId();
    const preselection = this.preselection();
    let items: DropdownItem<string>[] = [];

    if (projectId) {
      const tasksPage = this.tasksService.getProjectTasks(
        projectId,
        0,
        1000,
        TaskSortField.LastActivityAt,
        SortDirection.ASC,
        undefined,
        undefined
      )();

      if (tasksPage?.content) {
        items = tasksPage.content
          .map(taskSignal => taskSignal())
          .filter(Boolean)
          .map(task => ({ value: task.id, label: task.name }));
      }
    }

    return this.withPreselectedOption(items, preselection.taskId, preselection.taskName);
  });

  protected readonly locationItems: Signal<Location[]> = computed(() => {
    const projectId = this.projectId();
    if (!projectId) return [];

    const locations = this.locationsService.getProjectLocations(projectId)();
    return (locations ?? []).filter(loc => !loc.readOnly);
  });

  protected readonly allLocationItems: Signal<Location[]> = computed(() => {
    const roots = this.locationItems();
    return roots.flatMap(location =>
      location?.id ? [location, ...this.locationsService.getAllSubLocations(location.id)] : [location]
    );
  });

  protected readonly locationNodes = computed(() =>
    proffeoBuildTree(this.allLocationItems(), {
      getId: location => location.id ?? '',
      getParentId: location => location.parentLocation?.()?.id,
      getLabel: location => location.name ?? '',
      getValue: location => location.id ?? ''
    })
  );

  protected readonly hasLocations: Signal<boolean> = computed(() => {
    return this.locationItems().length > 0;
  });

  protected readonly tasksLoading: Signal<boolean> = computed(() => {
    const projectId = this.projectId();
    if (!projectId) return false;

    const tasksPage = this.tasksService.getProjectTasks(
      projectId,
      0,
      1000,
      TaskSortField.LastActivityAt,
      SortDirection.ASC,
      undefined,
      undefined
    )();

    return tasksPage === undefined;
  });

  protected readonly showNoProjectTasksState: Signal<boolean> = computed(() => {
    if (this.readonly() || this.tasksLoading()) {
      return false;
    }

    const projectId = this.projectId();
    if (!projectId) {
      return false;
    }

    return this.taskItems().length === 0;
  });

  protected readonly showLocationPicker: Signal<boolean> = computed(() => {
    const projectId = this.projectId();
    return !!projectId && this.hasLocations();
  });

  /** Summary label of the selected Project (falls back to the preselected name). */
  public readonly projectLabel: Signal<string> = computed(() => {
    const projectId = this.projectId();
    return this.projectItems().find(item => item.value === projectId)?.label ?? this.preselection().projectName ?? '';
  });

  /** Summary label of the selected Task (falls back to the preselected name). */
  public readonly taskLabel: Signal<string> = computed(() => {
    const taskId = this.taskId();
    return this.taskItems().find(item => item.value === taskId)?.label ?? this.preselection().taskName ?? '';
  });

  /** Full path of the selected Location, e.g. `Building / Floor 1`. */
  public readonly locationLabel: Signal<string> = computed(() => {
    const locationId = this.locationId();
    if (!locationId) {
      return '';
    }

    const location = this.locationsService.getById(locationId)();
    if (!location) {
      return '';
    }

    return location.locationsPath.map(loc => loc.name).join(' / ');
  });

  /** Company / Project / Task / Location sub-tree of the hosting form (rules: `projectTaskSchema`). */
  public readonly selection = input.required<FieldTree<ProjectTaskModel>>();
  public readonly readonly = input<boolean>(false);
  /** Labels of the preselected Company / Project / Task, shown before their option lists load. */
  public readonly preselection = input<ProjectTaskPreselection>({});
  /** The user picked another Company (Project, Task and Location were cleared). */
  public readonly companyChange = output<string | null>();

  public constructor() {
    effect(() => {
      const userId = this.authService.currentUser()?.id;
      if (userId) {
        untracked(() => this.companiesService.loadUserCompanies(userId));
      }
    });

    effect(() => {
      const companyId = this.companyId();
      if (companyId) {
        untracked(() => this.projectsService.loadCompanyProjects(companyId));
      }
    });

    effect(() => {
      const projectId = this.projectId();
      if (projectId) {
        untracked(() =>
          this.locationsService.loadProjectLocations(projectId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe()
        );
      }
    });
  }

  protected onCompanyChange(value: unknown): void {
    const company = selectedId(value);
    this.selection()().value.set({ company, project: null, task: null, location: null });
    this.companyChange.emit(company);
  }

  protected onProjectChange(value: unknown): void {
    const project = selectedId(value);
    this.selection()().value.update(current => ({ ...current, project, task: null, location: null }));
  }

  protected onTaskChange(value: unknown): void {
    const task = selectedId(value);
    this.selection()().value.update(current => ({ ...current, task, location: null }));
  }

  private withPreselectedOption(
    items: DropdownItem<string>[],
    value: string | undefined,
    label: string | undefined
  ): DropdownItem<string>[] {
    if (!value || !label || items.some(item => item.value === value)) {
      return items;
    }

    return [{ value, label }, ...items];
  }
}
